import { createFilmstripAdapter } from './filmstrip-adapter';
import { createPreviewController, type PreviewController } from './preview-controller';
import type { FotomaniaRuntime, PreviewSink } from './types';

export type BootSession = { disconnect(): void };

export type BootFactories = {
  createAdapter?: (element: Element, onUserSelection: (index: number) => void) => PreviewSink;
  createController?: (runtime: FotomaniaRuntime, sink: PreviewSink) => PreviewController;
};

type TrackedCustomizer = {
  element: Element;
  readyHandler: EventListener;
  disconnect(): void;
};

// Fotomania (feature, canvas soberano A1, Task 9 Step 4): disposal barrier. A customizer element
// removed and immediately re-added (SPA-style remount) must not get a new controller until the
// OLD controller's offscreen session has actually settled disposal -- otherwise two live sessions
// could race writes into one DOM sink. Module-scoped (not per-`track()` closure) so it survives
// across the old entry's teardown and the new entry's construction for the SAME element.
const pendingDisposal = new WeakMap<Element, Promise<void>>();

export function bootFilmstripPreviews(
  root: Document = document,
  factories: BootFactories = {},
): BootSession {
  const adapterFactory = factories.createAdapter ?? createFilmstripAdapter;
  const controllerFactory = factories.createController ?? createPreviewController;
  const tracked = new WeakMap<Element, TrackedCustomizer>();
  const active = new Set<TrackedCustomizer>();
  let disconnected = false;

  const track = (element: Element): void => {
    if (disconnected || tracked.has(element)) return;
    let controller: PreviewController | null = null;
    // Fotomania (feature, canvas soberano A1, Task 9 Step 4): re-entrancy guard. `activate` is
    // now async (it may await a pending disposal); a second trigger (e.g. the initial
    // already-ready fallback racing a `cvReady` event) must not start a second construction.
    let activating = false;
    // Fotomania (fix, canvas soberano A1, Task 9.1 SEV-1): per-entry identity. `disconnected`
    // (session-scoped) and the local `controller` (still null pre-construction) do NOT catch a
    // THIRD mutation: remove E -> re-add E (this entry, `activate` awaits the OLD entry's pending
    // disposal) -> remove E AGAIN before that disposal settles. The second `disconnect()` finds
    // `controller` still null (nothing to tear down) and just untracks -- it must also poison
    // THIS entry's own suspended `activate()` continuation, or it resumes after the await and
    // constructs a controller nobody tracks (a live orphan whose runtime listeners keep writing
    // into a sink in parallel with any future legitimate controller for the same element).
    let entryDisconnected = false;

    const activate = async (runtime: FotomaniaRuntime | undefined): Promise<void> => {
      if (disconnected || entryDisconnected || controller || activating || !runtime || typeof runtime.on !== 'function') {
        return;
      }
      activating = true;
      try {
        const priorDisposal = pendingDisposal.get(element);
        if (priorDisposal) {
          pendingDisposal.delete(element);
          await priorDisposal;
        }
        // Re-check identity AFTER the await: this entry (or the whole session) may have been
        // disconnected while suspended.
        if (disconnected || entryDisconnected || controller) return;
        const sink = adapterFactory(element, (index) => controller?.noteUserSelection(index));
        controller = controllerFactory(runtime, sink);
        controller.start();
      } finally {
        activating = false;
      }
    };

    const readyHandler: EventListener = (event) => {
      void activate((event as CustomEvent<{ fotomania?: FotomaniaRuntime }>).detail?.fotomania);
    };

    const entry: TrackedCustomizer = {
      element,
      readyHandler,
      disconnect(): void {
        entryDisconnected = true;
        element.removeEventListener('cvReady', readyHandler);
        if (controller) {
          const disposing = controller.whenDisposed();
          pendingDisposal.set(element, disposing);
          controller.disconnect();
        }
        controller = null;
        active.delete(entry);
        tracked.delete(element);
      },
    };

    tracked.set(element, entry);
    active.add(entry);
    element.addEventListener('cvReady', readyHandler);
    void activate((element as Element & { fotomania?: FotomaniaRuntime }).fotomania);
  };

  const customizersIn = (node: Node): Element[] => {
    if (!(node instanceof Element) && !(node instanceof Document)) return [];
    const found: Element[] = [];
    if (node instanceof Element && node.matches('fm-customizer')) found.push(node);
    found.push(...node.querySelectorAll('fm-customizer'));
    return found;
  };

  root.querySelectorAll('fm-customizer').forEach(track);

  const Observer = globalThis.MutationObserver;
  const observer =
    typeof Observer === 'function'
      ? new Observer((mutations) => {
          for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => customizersIn(node).forEach(track));
            mutation.removedNodes.forEach((node) => {
              customizersIn(node).forEach((element) => tracked.get(element)?.disconnect());
            });
          }
        })
      : null;

  if (observer && root.documentElement) {
    observer.observe(root.documentElement, { childList: true, subtree: true });
  }

  return {
    disconnect(): void {
      if (disconnected) return;
      disconnected = true;
      observer?.disconnect();
      [...active].forEach((entry) => entry.disconnect());
      active.clear();
    },
  };
}
